// Desc: Home page component
import { Button } from "@headlessui/react";
import { Spinner } from "_components";
import { ItineraryType } from "_interfaces";
import { API_ENDPOINT, formatCurrency } from "_lib/utils";
import { ConfigurationAtom, LeadFormAtom } from "_states";
import axios from "axios";
import parse from "html-react-parser";
import { MapPinIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";

/**
 * ItineraryPage component
 * @returns {JSX.Element} JSX.Element
 */
const ItineraryPage = () => {
  const params = useParams();
  const configurations = useRecoilValue(ConfigurationAtom);
  const [loading, setLoading] = useState(true);
  const [itinerary, setItinerary] = useState<ItineraryType>();
  const setLeadFormOpen = useSetRecoilState(LeadFormAtom);
  const days = [1, 2, 3, 4, 5];

  useEffect(() => {
    setLoading(true);
    const tour_id = params.param;
    axios
      .get(
        `${API_ENDPOINT}/items/tours/${tour_id}?fields=*,destinations.destinations_id.name`
      )
      .then((res) => {
        const data = res.data.data;
        data.destinations = data.destinations
          .map((destination: any) => destination.destinations_id.name)
          .join(", ");
        setItinerary(data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [params]);

  return (
    <>
      <div
        className="flex flex-col items-center justify-center h-[400px] bg-center bg-cover bg-no-repeat w-full text-white lg:space-y-6 space-y-4 text-center"
        style={{ backgroundImage: "url('/images/banner-1.jpg')" }}
      >
        <h2 className="text-3xl px-2">Tour Details</h2>
      </div>
      {/* Itinerary section */}
      {loading ? (
        <Spinner />
      ) : (
        itinerary && (
          <section className="max-w-7xl mx-auto space-y-8 py-24 md:px-2 sm:px-4 px-4">
            <div>
              <h1 className="text-4xl font-bold mb-4">{itinerary?.name}</h1>
              <p className="flex items-start font-medium gap-1">
                <MapPinIcon className="h-5" />
                {itinerary?.destinations}
              </p>
            </div>
            <div className="grid lg:grid-cols-4 gap-5">
              {/* Itinerary Details */}
              <div className="lg:col-span-3 space-y-5">
                <div>
                  <img
                    src={`${API_ENDPOINT}/assets/${itinerary?.image}/${itinerary?.name}.jpg`}
                    alt="singapore"
                    className="w-full h-[440px] object-cover object-center rounded-lg"
                  />
                </div>
                <div className="rounded-lg shadow-lg border p-6">
                  <h3 className="text-2xl font-semibold border-b border-primary w-fit mb-4">
                    Overview
                  </h3>
                  <p className="text-slate-500 text-lg font-medium leading-8 whitespace-pre-wrap">
                    {itinerary?.overview}
                  </p>
                </div>
                <div className="rounded-lg shadow-lg border p-6">
                  <h3 className="text-2xl font-semibold border-b border-primary w-fit mb-4">
                    Itinerary
                  </h3>
                  {itinerary?.itinerary.map((day, index) => (
                    <div className="border-t py-4" key={index}>
                      <h4 className="text-lg font-semibold">
                        Day {index + 1}: {day.day_title}
                      </h4>
                      <div className="text-slate-500 render mt-2">
                        {parse(day.day_summary)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg shadow-lg border p-6">
                  <h3 className="text-2xl font-semibold border-b border-primary w-fit mb-4">
                    Included / Excluded
                  </h3>
                  <div className="text-slate-500 font-medium render">
                    {parse(itinerary?.inclusion_exclusion || "")}
                  </div>
                </div>
                <div>
                  <Button
                    className="text-primary font-bold py-1 rounded-lg"
                    onClick={() => {
                      setLeadFormOpen(true);
                    }}
                  >
                    Connect with us for any queries
                  </Button>
                </div>
              </div>

              {/* Sidebar for Package Details */}
              <div className="w-full space-y-5">
                <Button
                  className="w-full bg-primary text-white font-bold px-8 py-3 rounded-lg"
                  onClick={() => {
                    setLeadFormOpen(true);
                  }}
                >
                  Book now
                </Button>
                <div className="rounded-lg shadow-xl border p-6">
                  <h3 className="font-semibold text-lg border-b border-primary w-fit mb-6">
                    Package Details
                  </h3>
                  <div className="mb-4">Valid throughout the year</div>
                  <div className="font-medium space-y-2 py-2">
                    <p>
                      <span className="font-bold text-slate-700">
                        Duration:
                      </span>{" "}
                      {(itinerary?.nights || 0) + 1} days {itinerary?.nights}{" "}
                      nights
                    </p>
                    <p>
                      <span className="font-bold text-slate-700">
                        Location:
                      </span>{" "}
                      {itinerary?.destinations}
                    </p>
                    <p>
                      <span className="font-bold text-slate-700">
                        Tour Type:
                      </span>{" "}
                      Group
                    </p>
                    <p>
                      <span className="font-bold text-slate-700">
                        Group size:
                      </span>{" "}
                      10-15
                    </p>
                  </div>
                  <h3 className="font-semibold text-lg border-b border-primary w-fit mt-6 mb-3">
                    Price
                  </h3>
                  <p className="text-lg text-primary font-bold">
                    {formatCurrency(itinerary?.starting_price)}
                    <span className="font-normal text-slate-700 text-sm ms-2">
                      per person
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </section>
        )
      )}

      {/* Top Destinations section */}
      <section className="max-w-7xl mx-auto space-y-8 pb-36 md:px-2 sm:px-4 px-4">
        <div>
          <h2 className="font-semibold text-4xl text-center mb-10">
            Our top destinations
          </h2>
          <div className="grid lg:grid-cols-5 sm:grid-cols-2 grid-cols-1 gap-6">
            {configurations.featured_destinations
              .slice(0, 5)
              .map((destination, key) => (
                <Link
                  key={key}
                  to={`/tours/${destination.slug}`}
                  className="relative flex justify-center items-center rounded-lg overflow-hidden aspect-video md:aspect-square"
                >
                  <div
                    className="h-full w-full transition ease-in-out duration-500 hover:scale-110 bg-center bg-cover hover:brightness-50 brightness-75"
                    style={{
                      backgroundImage: `url(\'${destination.image}\')`,
                    }}
                  ></div>
                  <div className="absolute text-white pointer-events-none">
                    <h3 className="text-2xl">{destination.name}</h3>
                  </div>
                </Link>
              ))}
          </div>
        </div>
        <div className="text-center">
          <Link
            to="/destinations"
            className="bg-primary text-white font-medium px-8 py-3 rounded-lg"
          >
            View all destinations
          </Link>
        </div>
      </section>
    </>
  );
};

export default ItineraryPage;
